import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import AssistantTrigger from "./AssistantTrigger";
import { useAssistantChat } from "./api/assistant";
import { Message } from "./api/assistant";
import ChatHeader from "./ChatHeader";
import ChatMessage from "./ChatMessage";
import ChatInput, { ChatInputHandle } from "./ChatInput";
import FeedbackUI from "./FeedbackUI";
import TypingIndicator from "./TypingIndicator";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import { useTheme } from "@mui/material/styles";
import {
  AssistantToolsProvider,
  useAssistantCommands,
  assistantTools,
} from "./AssistantTools";
import { classNames } from "../../utils/classNames";
import Icon from "../../components/Icon";
import PendingMessage from "../../components/PendingMessage";
import MessageSources from "../../components/MessageSources";
import RecommendationCards from "../../components/RecommendationCards";
import MessageFeedback from "../../components/MessageFeedback";
import ResourcesSection from "../../components/ResourcesSection";
import LoadingBubble from "../../components/LoadingBubble";
import Lottie from "lottie-react-web";
import { v4 as uuidv4 } from "uuid";
import BuddiesList from "./BuddiesList";
import SelectedBuddyInfo from "./SelectedBuddyInfo";

// Import space-themed components
const AstronautLottie = dynamic(() => import("../AstronautLottie"), {
  ssr: false,
});

// Custom hook for responsive design
function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }

    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);

    return () => media.removeEventListener("change", listener);
  }, [matches, query]);

  return matches;
}

// Custom hook to detect when mobile keyboard is visible
function useKeyboardVisibility() {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Visual Viewport API is the most reliable for detecting keyboard
    const detectKeyboard = () => {
      if (window.visualViewport) {
        // If the visual viewport height is significantly less than the window height
        // it usually means the keyboard is shown
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const heightDifference = windowHeight - viewportHeight;

        // Consider the keyboard visible if height difference is more than 150px
        setIsKeyboardVisible(heightDifference > 150);
      }
    };

    // Add event listeners to detect viewport changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", detectKeyboard);
      window.visualViewport.addEventListener("scroll", detectKeyboard);
    } else {
      // Fallback for browsers without Visual Viewport API
      window.addEventListener("resize", detectKeyboard);
    }

    // Initial check
    detectKeyboard();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", detectKeyboard);
        window.visualViewport.removeEventListener("scroll", detectKeyboard);
      } else {
        window.removeEventListener("resize", detectKeyboard);
      }
    };
  }, []);

  return isKeyboardVisible;
}

// Define types for our components
interface StarStyle extends React.CSSProperties {
  left: string;
  top: string;
  width: string;
  height: string;
  opacity: number;
  animationDuration: string;
}

// Star component for background
const Star = React.memo(({ style }: { style: StarStyle }) => (
  <span
    className="blinking-star"
    style={{
      position: "absolute",
      // Reduce brightness for the star background to be less harsh
      backgroundColor: "rgba(255, 255, 255, 0.7)", // Slightly dimmer white
      boxShadow:
        "rgba(255, 255, 255, 0.5) 0px 0px 2px, rgba(255, 255, 255, 0.3) 0px 0px 5px, rgba(180, 230, 255, 0.3) 0px 0px 10px", // Softer glow
      borderRadius: "50%",
      ...style,
      // Reduce opacity overall for stars
      opacity: style.opacity * 0.7, // Make all stars slightly more subtle
    }}
  />
));
Star.displayName = "Star";

// Planet component
const Planet = React.memo(
  ({
    type,
    style,
    className,
  }: {
    type: string;
    style: React.CSSProperties;
    className?: string;
  }) => {
    const planetSrc = `/assets/onboarding/planet_${type}.svg`;

    if (isNaN(+type)) {
      return null;
    }

    return (
      <div
        className={`absolute pointer-events-none ${className || ""}`}
        style={{
          ...style,
          transition: "all 0.5s ease",
        }}
      >
        <img
          src={planetSrc}
          alt={`Planet ${type}`}
          className="w-full h-full object-contain"
        />
      </div>
    );
  }
);
Planet.displayName = "Planet";

// Memoized Chat Header component
const MemoizedChatHeader = React.memo(ChatHeader);
MemoizedChatHeader.displayName = "MemoizedChatHeader";

// Memoized Chat Input component
const MemoizedChatInput = React.memo(ChatInput);
MemoizedChatInput.displayName = "MemoizedChatInput";

// Memoized AssistantTrigger component
const MemoizedAssistantTrigger = React.memo(AssistantTrigger);
MemoizedAssistantTrigger.displayName = "MemoizedAssistantTrigger";

// Memoized Message List component
const MessageList = React.memo(
  ({
    messages,
    showFeedbackFor,
    setShowFeedbackFor,
    addAssistantResponseMutate,
    createBlazeSession,
    getLastUserQuery,
    blazeQueryLoading,
    recommendationsLoading,
    requestCancelled,
    retryResourceFetch,
    messagesEndRef,
    handleToolCall,
    selectedBuddyId,
    handleSelectBuddy,
  }: {
    messages: Message[];
    showFeedbackFor: string | null;
    setShowFeedbackFor: (id: string | null) => void;
    addAssistantResponseMutate: any;
    createBlazeSession: (query: string) => void;
    getLastUserQuery: () => string;
    blazeQueryLoading: boolean;
    recommendationsLoading: boolean;
    requestCancelled: boolean;
    retryResourceFetch: () => void;
    messagesEndRef: React.RefObject<HTMLDivElement>;
    handleToolCall: (message: string) => Promise<void>;
    selectedBuddyId: string | null;
    handleSelectBuddy: (buddyId: string) => void;
  }) => {
    useEffect(() => {
      console.log("Test | messages - ", messages);
    }, [messages]);

    // Process the last message if it's from the assistant
    useEffect(() => {
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];

        // Only process tool calls from the old string format (!tool[[...]])
        // Skip function_calls array processing as it's handled by the new system in assistant.ts
        console.log("MessageList useEffect - checking message:", {
          sender: lastMessage.sender,
          hasToolCall: !!lastMessage.metadata?.tool_call,
          hasFunctionCalls: !!lastMessage.metadata?.function_calls,
          hasToolCalls: !!lastMessage.tool_calls,
          text: lastMessage.text?.substring(0, 100) + "...",
        });

        // Skip processing if this message has function_calls (handled by new system)
        if (
          lastMessage.metadata?.function_calls &&
          Array.isArray(lastMessage.metadata.function_calls)
        ) {
          console.log(
            "Skipping tool processing - function_calls handled by new system"
          );
          return;
        }

        // Skip processing if this message has tool_calls array (already processed by new system)
        if (lastMessage.tool_calls && Array.isArray(lastMessage.tool_calls)) {
          console.log(
            "Skipping tool processing - tool_calls handled by new system"
          );
          return;
        }

        // Only process old-style tool_call strings from metadata
        if (
          lastMessage.sender === "assistant" &&
          lastMessage.metadata?.tool_call &&
          typeof lastMessage.metadata.tool_call === "string"
        ) {
          console.log(
            "Found tool_call in metadata:",
            lastMessage.metadata.tool_call
          );
          handleToolCall(lastMessage.metadata.tool_call);
          return;
        }

        // Only process old-style !tool[[...]] strings in text content
        if (lastMessage.sender === "assistant" && lastMessage.text) {
          const hasOldStyleToolCall = lastMessage.text.includes("!tool[[");
          console.log(
            "Checking text for old-style tool calls:",
            hasOldStyleToolCall
          );
          if (hasOldStyleToolCall) {
            handleToolCall(lastMessage.text);
          }
        }
      }
    }, [messages, handleToolCall]);

    // Function to check if a message is a welcome message
    const isWelcomeMessage = (message: Message) => {
      return (
        message.sender === "assistant" &&
        (message.text.includes("Welcome to PuStack AI") ||
          message.text.includes("Welcome back!") ||
          message.text.includes("I'm your AI assistant"))
      );
    };

    return (
      <>
        {messages
          // Filter out duplicate consecutive cancellation messages with same error_type
          .filter((message, index, array) => {
            // If this is a cancellation message, check if the previous message is also a cancellation
            if (
              message.messageType === "cancellation" &&
              message.metadata?.error_type === "user_cancelled" &&
              index > 0
            ) {
              const prevMessage = array[index - 1];
              // Only filter out if the previous message is also a cancellation with the same error_type
              // AND they're immediate duplicates with nearly identical timestamps (within 1 second)
              if (
                prevMessage.messageType === "cancellation" &&
                prevMessage.metadata?.error_type === "user_cancelled"
              ) {
                // Check timestamps to determine if they're likely for the same cancellation action
                const currentTime = new Date(message.timestamp).getTime();
                const prevTime = new Date(prevMessage.timestamp).getTime();
                const timeDiff = Math.abs(currentTime - prevTime);

                // Only filter out if the messages were created very close together (within 1 second)
                return timeDiff > 1000;
              }
            }
            // Keep all other messages
            return true;
          })
          .map((message, index) => {
            // Check if this message is part of a consecutive sequence from the same sender
            const isConsecutive =
              index > 0 && messages[index - 1].sender === message.sender;

            // Check if this is the last message in a group
            const isLastInGroup =
              index === messages.length - 1 ||
              messages[index + 1].sender !== message.sender;

            // Check if this is a streaming message
            const isStreaming = message.metadata?.isStreaming === true;

            if (isStreaming && !message.metadata?.isSearching) {
              return <TypingIndicator key={message.id} />;
            }

            // Apply special styling for welcome messages
            if (isWelcomeMessage(message)) {
              return (
                <div key={message.id} className="mb-2">
                  <div className="bg-gradient-to-r from-gray-900/80 to-black/70 p-2 rounded-lg border border-gray-800 shadow-lg relative overflow-hidden">
                    {/* Simpler animated background for welcome message */}
                    <div className="absolute inset-0 overflow-hidden">
                      {/* Fewer animated star particles */}
                      {[...Array(5)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute w-1 h-1 rounded-full"
                          style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            backgroundColor: [
                              "#3B82F6",
                              "#8B5CF6",
                              "#EC4899",
                              "#38BDF8",
                              "#FB923C",
                            ][Math.floor(Math.random() * 5)],
                            boxShadow: "0 0 4px currentColor",
                          }}
                          animate={{
                            opacity: [0.3, 0.8, 0.3],
                            scale: [1, 1.3, 1],
                          }}
                          transition={{
                            duration: 2 + Math.random() * 2,
                            repeat: Infinity,
                            repeatType: "reverse",
                            delay: Math.random() * 2,
                          }}
                        />
                      ))}
                    </div>

                    <div className="relative z-10">
                      {/* Parse welcome message text for custom formatting */}
                      {(() => {
                        // Split the message into parts
                        const parts = message.text.split("\n\n");

                        // Extract the header (first part with asterisks)
                        const headerMatch = parts[0].match(/\*(.*?)\*/);
                        const header = headerMatch ? headerMatch[1] : parts[0];

                        // Get the introduction text
                        const introText = parts[1] || "";

                        // Extract bullet points if they exist (starting with •)
                        const hasBulletPoints = message.text.includes("•");
                        const bulletPoints = hasBulletPoints
                          ? message.text
                              .split("\n")
                              .filter((line) => line.includes("•"))
                          : [];

                        // Extract intro text without bullets
                        const cleanIntroText =
                          hasBulletPoints && introText
                            ? introText
                                .split("\n")
                                .filter((line) => !line.includes("•"))
                                .join(" ")
                            : introText;

                        return (
                          <>
                            {/* Header and intro in a horizontal layout */}
                            <div className="flex items-center mb-2">
                              <div className="flex items-center mr-3">
                                <motion.span
                                  className="text-yellow-300 mr-1 text-base"
                                  animate={{ rotate: [0, 15, 0] }}
                                  transition={{ duration: 2, repeat: Infinity }}
                                >
                                  ✨
                                </motion.span>
                                <h3 className="text-sm font-semibold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
                                  {header}
                                </h3>
                              </div>

                              <p className="text-xs text-gray-300 flex-1">
                                Hey there! I can help with homework, explain
                                concepts, and more.
                              </p>
                            </div>

                            {/* Bullet points in horizontal scrollable layout */}
                            {bulletPoints.length > 0 && (
                              <div className="flex gap-1.5 mb-2 overflow-x-auto pb-1 hide-scrollbar">
                                {bulletPoints.slice(0, 5).map((point, idx) => {
                                  const text = point.replace("•", "").trim();
                                  // Extract just the key concept from each bullet
                                  const shortText = text
                                    .split(" ")
                                    .slice(0, 3)
                                    .join(" ");
                                  const icons = ["📚", "🔍", "🧩", "✏️", "💡"];
                                  const colors = [
                                    "text-blue-300",
                                    "text-purple-300",
                                    "text-cyan-300",
                                    "text-amber-300",
                                    "text-emerald-300",
                                  ];

                                  return (
                                    <motion.div
                                      key={idx}
                                      className="flex items-center flex-shrink-0 bg-black/20 rounded-md px-2 py-1"
                                      initial={{ y: 5, opacity: 0 }}
                                      animate={{ y: 0, opacity: 1 }}
                                      transition={{ delay: idx * 0.05 }}
                                      whileHover={{
                                        backgroundColor:
                                          "rgba(255,255,255,0.1)",
                                        y: -2,
                                        transition: { duration: 0.2 },
                                      }}
                                    >
                                      <motion.span
                                        className={`${
                                          colors[idx % colors.length]
                                        } text-base mr-1.5`}
                                        animate={{ y: [0, -2, 0] }}
                                        transition={{
                                          duration: 1.5,
                                          repeat: Infinity,
                                          delay: idx * 0.1,
                                        }}
                                      >
                                        {icons[idx % icons.length]}
                                      </motion.span>
                                      <span className="text-[0.65rem] text-white whitespace-nowrap">
                                        {shortText}
                                      </span>
                                    </motion.div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Example questions in a horizontal layout */}
                            <div className="grid grid-cols-2 gap-1.5 mb-2">
                              {(selectedBuddyId === "blaze"
                                ? [
                                    "Connect me with a Math tutor",
                                    "Help with my science homework",
                                    "I need expert help with calculus",
                                    "Find a tutor for organic chemistry",
                                  ]
                                : selectedBuddyId === "live"
                                ? [
                                    "Show upcoming Physics classes",
                                    "Find live classes on algebra",
                                    "Are there any Chemistry classes today?",
                                    "Join a Science live class",
                                  ]
                                : selectedBuddyId === "exercise"
                                ? [
                                    "Give me practice problems on polynomials",
                                    "I need exercises for Newton's laws",
                                    "Practice questions for chemical bonding",
                                    "Help me practice trigonometry",
                                  ]
                                : selectedBuddyId === "quiz"
                                ? [
                                    "Help me prepare for my Science test",
                                    "Quiz me on Indian history",
                                    "Practice questions for my Math exam",
                                    "Test my knowledge of the periodic table",
                                  ]
                                : selectedBuddyId === "tips"
                                ? [
                                    "Study tips for better memorization",
                                    "How to prepare for exams effectively",
                                    "Tips for solving word problems",
                                    "Best way to learn formulas",
                                  ]
                                : selectedBuddyId === "explainer"
                                ? [
                                    "Explain photosynthesis in detail",
                                    "Give me a thorough explanation of atoms",
                                    "How do black holes work exactly?",
                                    "I need an in-depth explanation of calculus",
                                  ]
                                : [
                                    "Explain photosynthesis simply",
                                    "Help me with fractions",
                                    "What is the water cycle?",
                                    "How do I solve 2x + 5 = 13?",
                                  ]
                              ).map((example, idx) => (
                                <motion.div
                                  key={idx}
                                  className="text-[0.65rem] text-gray-300 bg-gray-800/40 px-2 py-1 rounded-md flex items-center cursor-pointer hover:bg-gray-700/40"
                                  whileHover={{ x: 3 }}
                                  onClick={() => {
                                    const event = new CustomEvent(
                                      "assistantFollowUpQuestion",
                                      {
                                        detail: { question: example },
                                      }
                                    );
                                    window.dispatchEvent(event);
                                  }}
                                >
                                  <span className="text-xs mr-1.5 text-blue-300">
                                    •
                                  </span>
                                  <span className="line-clamp-1">
                                    {example}
                                  </span>
                                </motion.div>
                              ))}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <ChatMessage
                key={message.id}
                message={message}
                isConsecutive={isConsecutive}
                isLastInGroup={isLastInGroup}
                resourcesLoading={
                  index === messages.length - 1 &&
                  message.sender === "assistant" &&
                  message.messageType !== "blazeSessionConfirmation" &&
                  recommendationsLoading
                }
                requestCancelled={
                  index === messages.length - 1 &&
                  message.sender === "assistant" &&
                  message.messageType !== "blazeSessionConfirmation" &&
                  requestCancelled
                }
                retryResourceFetch={retryResourceFetch}
                selectedBuddyId={selectedBuddyId}
              />
            );
          })}

        {/* Feedback UI */}
        {showFeedbackFor && !blazeQueryLoading && !recommendationsLoading && (
          <FeedbackUI
            showFeedbackFor={showFeedbackFor}
            setShowFeedbackFor={setShowFeedbackFor}
            addAssistantResponseMutate={addAssistantResponseMutate}
            createBlazeSession={createBlazeSession}
            getLastUserQuery={getLastUserQuery}
            lastMessageType={
              messages.length > 0
                ? messages[messages.length - 1].messageType
                : null
            }
            sessionRequestData={
              messages.length > 0 &&
              (messages[messages.length - 1].metadata?.show_feedback_ui ||
                messages[messages.length - 1].show_feedback_ui ||
                String(
                  messages[messages.length - 1].metadata?.response_type
                ).includes("blaze_session_request") ||
                String(messages[messages.length - 1].response_type).includes(
                  "blaze_session_request"
                ))
                ? messages[messages.length - 1].metadata
                    ?.session_request_data ||
                  messages[messages.length - 1].session_request_data
                : undefined
            }
            selectedBuddyId={selectedBuddyId}
            handleSelectBuddy={handleSelectBuddy}
          />
        )}

        <div id="messages-end-ref" ref={messagesEndRef} />
      </>
    );
  }
);
MessageList.displayName = "MessageList";

// Memoized Empty State component
const EmptyStateUI = React.memo(
  ({
    addMessage,
    selectedBuddyId,
  }: {
    addMessage: any;
    selectedBuddyId: string | null;
  }) => {
    // Map of buddy-specific questions
    const buddyQuestions = {
      b1: [
        "Explain the difference between covalent and ionic bonds",
        "How does photosynthesis work?",
        "What are Newton's laws of motion?",
        "Tell me about the periodic table",
      ],
      b2: [
        "Help me solve a quadratic equation",
        "Explain the Pythagorean theorem",
        "What is calculus used for?",
        "How do I find the derivative of a function?",
      ],
      b3: [
        "What caused World War I?",
        "Tell me about the Industrial Revolution",
        "Who was Mahatma Gandhi?",
        "Explain the Cold War",
      ],
      default: [
        "Explain Newton's laws of motion",
        "Connect me with a Math tutor",
        "Switch my grade to 10th",
        "Tell me about PuStack Pro",
      ],
    };

    // Get questions for the selected buddy or use default
    const questions =
      selectedBuddyId && buddyQuestions[selectedBuddyId]
        ? buddyQuestions[selectedBuddyId]
        : buddyQuestions["default"];

    // Get buddy name for display
    const buddyNames = {
      b1: "Science Buddy",
      b2: "Math Buddy",
      b3: "History Buddy",
      default: "AI Assistant",
    };

    const buddyName =
      selectedBuddyId && buddyNames[selectedBuddyId]
        ? buddyNames[selectedBuddyId]
        : buddyNames["default"];

    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-200 relative z-10">
        <div className="p-3 bg-black/70 backdrop-blur-sm rounded-xl shadow-lg border border-gray-800 w-full overflow-hidden relative">
          {/* Simplified animated background */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  backgroundColor: [
                    "#3B82F6",
                    "#8B5CF6",
                    "#EC4899",
                    "#10B981",
                    "#F59E0B",
                  ][Math.floor(Math.random() * 5)],
                  boxShadow: "0 0 8px currentColor",
                }}
                animate={{
                  opacity: [0.2, 0.8, 0.2],
                  scale: [1, 1.5, 1],
                }}
                transition={{
                  duration: 2 + Math.random() * 3,
                  repeat: Infinity,
                  repeatType: "reverse",
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>

          <div className="flex items-center mb-3">
            <div className="w-12 h-12 relative flex-shrink-0 mr-3">
              <motion.div
                className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-70"
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 10, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <AstronautLottie
                style={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  zIndex: 10,
                }}
              />
            </div>
            <div className="flex-1">
              <motion.h3
                className="font-medium text-white text-base mb-0.5"
                animate={{
                  y: [0, -2, 0],
                  scale: [1, 1.02, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                {buddyName}
              </motion.h3>
              <p className="text-xs text-gray-300 relative z-10">
                {selectedBuddyId === "blaze"
                  ? "I can connect you with expert tutors for personalized help"
                  : selectedBuddyId === "live"
                  ? "I can help you find and join interactive live classes"
                  : selectedBuddyId === "exercise"
                  ? "I can assist with practice problems and exercises"
                  : selectedBuddyId === "quiz"
                  ? "I can help you prepare for quizzes and tests"
                  : selectedBuddyId === "tips"
                  ? "I can provide study tips and learning strategies"
                  : "I can help with homework, explain concepts, and answer questions"}
              </p>
            </div>
          </div>

          {/* Example queries based on selected buddy - using horizontal space */}
          <div className="space-y-1.5 mb-2 relative z-10">
            <p className="text-xs text-gray-400 mb-1 font-medium">
              Try asking me:
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                ...(selectedBuddyId === "blaze"
                  ? [
                      "Connect me with a Math tutor",
                      "Help with my science homework",
                      "I need expert help with calculus",
                      "Find a tutor for organic chemistry",
                    ]
                  : selectedBuddyId === "live"
                  ? [
                      "Show upcoming Physics classes",
                      "Find live classes on algebra",
                      "Are there any Chemistry classes today?",
                      "When is the next Biology class?",
                    ]
                  : selectedBuddyId === "exercise"
                  ? [
                      "Give me practice problems on polynomials",
                      "I need exercises for Newton's laws",
                      "Practice questions for chemical bonding",
                      "Help me practice trigonometry",
                    ]
                  : selectedBuddyId === "quiz"
                  ? [
                      "Help me prepare for my Science test",
                      "Quiz me on Indian history",
                      "Practice questions for my Math exam",
                      "Test my knowledge of the periodic table",
                    ]
                  : selectedBuddyId === "tips"
                  ? [
                      "Study tips for better memorization",
                      "How to prepare for exams effectively",
                      "Tips for solving word problems",
                      "Best way to learn formulas",
                    ]
                  : selectedBuddyId === "explainer"
                  ? [
                      "Explain the theory of relativity in detail",
                      "I need an in-depth explanation of photosynthesis",
                      "Tell me everything about cellular respiration",
                      "Provide a comprehensive explanation of plate tectonics",
                    ]
                  : [
                      "Explain photosynthesis simply",
                      "Help me understand fractions",
                      "What is Newton's third law?",
                      "Solve 2x + 5 = 15",
                      "Tell me about the water cycle",
                    ]),
              ]
                .slice(0, 4)
                .map((question, idx) => (
                  <motion.div
                    key={idx}
                    className="text-[0.7rem] text-gray-300 bg-gray-800/40 px-2 py-1.5 rounded-md flex items-center cursor-pointer hover:bg-gray-700/40"
                    whileHover={{ x: 3 }}
                    onClick={() => {
                      addMessage.mutate({
                        text: question,
                        sender: "user",
                      });
                    }}
                  >
                    <span className="text-xs mr-1.5 text-blue-300">•</span>
                    <span className="line-clamp-1">{question}</span>
                  </motion.div>
                ))}
            </div>
          </div>

          <div className="flex justify-center mt-1 relative z-10">
            <motion.button
              onClick={() => {
                const randomQuestion =
                  questions[Math.floor(Math.random() * questions.length)];
                addMessage.mutate({
                  text: randomQuestion,
                  sender: "user",
                });
              }}
              className="text-xs bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-3 py-1 rounded-md border border-indigo-800 shadow-lg"
              whileHover={{
                scale: 1.05,
                boxShadow: "0 0 10px rgba(99, 102, 241, 0.5)",
              }}
              whileTap={{ scale: 0.95 }}
            >
              Ask something
            </motion.button>
          </div>
        </div>
      </div>
    );
  }
);
EmptyStateUI.displayName = "EmptyStateUI";

function AssistantContent({
  chatContainerRef,
  messagesEndRef,
  scrollPosition,
  setScrollPosition,
  selectedBuddyId,
  handleSelectBuddy,
  isExpanded,
  handleToggleExpand,
}: {
  chatContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  scrollPosition: number;
  setScrollPosition: (position: number) => void;
  selectedBuddyId: string | null;
  handleSelectBuddy: (buddyId: string) => void;
  isExpanded: boolean;
  handleToggleExpand: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showFeedbackFor, setShowFeedbackFor] = useState<string | null>(null);
  const [rocketLaunch, setRocketLaunch] = useState<any>(null);
  const chatInputRef = useRef<ChatInputHandle>(null);
  const assistantRef = useRef<HTMLDivElement>(null); // Add this new ref
  const [inputValue, setInputValue] = useState("");

  // Add tool execution state
  const [toolExecution, setToolExecution] = useState<{
    isExecuting: boolean;
    toolName: string;
  }>({
    isExecuting: false,
    toolName: "",
  });

  // Track assistant state (idle, generating, searching, or tool_execution)
  const [assistantState, setAssistantState] = useState({
    status: "idle",
    query: "",
  });

  const router = useRouter();
  const { executeCommand } = useAssistantCommands();

  // Check if we're on mobile
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Check if keyboard is visible on mobile
  const isKeyboardVisible = useKeyboardVisibility();

  // Process tool calls from assistant messages
  const handleToolCall = useCallback(
    async (message: string) => {
      try {
        console.log("handleToolCall called with message:", message);

        // Enhanced regex pattern to match tool calls more reliably
        // Support both formats: !tool[[toolName:param=value]] and direct tool_calls
        const toolCallRegex = /!tool\[\[(.*?)\]\]/g;
        const matches = [...message.matchAll(toolCallRegex)];

        console.log("Tool call matches found:", matches.length);

        // Create a set to track processed tool calls to prevent duplicates
        const processedToolCalls = new Set();

        // If there are no regex matches, also check for direct tool_calls
        if (matches.length === 0) {
          console.log("No regex matches found, checking message object");
          // This would be handled by the MessageList component's useEffect
          return;
        }

        // Process each match (previously only processed the first one)
        for (const match of matches) {
          const toolCall = match[1]; // The captured tool call string

          // Skip if we've already processed this exact tool call
          if (processedToolCalls.has(toolCall)) {
            console.log("Skipping duplicate tool call:", toolCall);
            continue;
          }

          // Mark as processed
          processedToolCalls.add(toolCall);

          console.log("Processing tool call:", toolCall);

          // Safely parse the tool call (format: toolName:param1=value1,param2=value2)
          const parts = toolCall.split(":");
          if (parts.length < 2) {
            console.error("Invalid tool call format:", toolCall);
            continue; // Skip this tool call and move to the next one
          }

          const toolName = parts[0].trim();
          const paramsString = parts.slice(1).join(":"); // Handle cases where values might contain colons

          console.log("Tool name:", toolName, "Params string:", paramsString);

          if (toolName && paramsString) {
            try {
              // Parse params more robustly
              const params = paramsString.split(",").reduce((acc, param) => {
                const equalsIndex = param.indexOf("=");
                if (equalsIndex > 0) {
                  const key = param.substring(0, equalsIndex).trim();
                  const value = param.substring(equalsIndex + 1).trim();
                  if (key && value !== undefined) {
                    acc[key] = value;
                  }
                }
                return acc;
              }, {} as Record<string, string>);

              console.log("Parsed params:", params);

              // Set tool execution state to show animation
              setToolExecution({
                isExecuting: true,
                toolName: toolName,
              });

              // Set assistant state to tool_execution
              setAssistantState((prev) => ({
                ...prev,
                status: "tool_execution",
              }));

              // Execute the command with timeout protection
              console.log("Executing command:", toolName, params);
              try {
                // Add a timeout for tool execution - fail after 20 seconds
                const timeoutPromise = new Promise<any>((_, reject) => {
                  setTimeout(
                    () => reject(new Error("Tool execution timed out")),
                    20000
                  );
                });

                await Promise.race([
                  executeCommand(toolName, params),
                  timeoutPromise,
                ]);

                console.log("Command executed successfully");
              } catch (execError) {
                console.error("Error during command execution:", execError);
                // Don't rethrow, so we can continue processing other tools
              } finally {
                // Reset tool execution state regardless of success/failure
                setToolExecution({
                  isExecuting: false,
                  toolName: "",
                });

                // Reset assistant state
                setAssistantState((prev) => ({ ...prev, status: "idle" }));
              }
            } catch (parseError) {
              console.error("Error parsing tool parameters:", parseError);
            }
          }
        }
      } catch (error) {
        console.error("Error processing tool call:", error);
        // Reset states in case of error
        setToolExecution({
          isExecuting: false,
          toolName: "",
        });
        setAssistantState((prev) => ({ ...prev, status: "idle" }));
      }
    },
    [executeCommand, setAssistantState]
  );

  // Load rocket animation
  useEffect(() => {
    const loadRocketAnimation = async () => {
      try {
        const rocketJson = await import(
          "../../public/assets/lottie/launch-rocket.json"
        );
        setRocketLaunch(rocketJson.default);
      } catch (error) {
        console.error("Failed to load rocket animation:", error);
      }
    };

    loadRocketAnimation();
  }, []);

  // Handle scrolling to bottom when keyboard appears
  useEffect(() => {
    if (isKeyboardVisible && messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          block: "end",
          behavior: "smooth",
        });
      }, 100);
    }
  }, [isKeyboardVisible]);

  // Follow-up question handler will be added after variables are defined

  // Use our custom hook for chat functionality
  const {
    messages: assistantMessages,
    isLoading,
    addMessage,
    addAssistantResponse: { mutate: addAssistantResponseMutate },
    isAuthenticated,
    blazeQueryLoading,
    recommendationsLoading,
    interruptRequest,
    requestCancelled,
    retryResourceFetch,
    conversationHistory, // Get conversation history
  } = useAssistantChat();

  // Keep track of which messages we've already scrolled for
  const scrolledMessageIds = useRef<Set<string>>(new Set());

  // In the toggleChat function, reset the tool execution state when toggling
  const toggleChat = useCallback(() => {
    // If we're closing the assistant, reset the tool execution state
    if (isOpen) {
      setToolExecution({
        isExecuting: false,
        toolName: "",
      });
      setAssistantState((prev) => ({
        ...prev,
        status: "idle",
      }));
    }
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  // The sendMessage function in the AssistantContent component
  // This function should be within the component where selectedBuddyId is defined
  const sendMessage = useCallback(async () => {
    if (inputValue.trim() === "") return;

    // Clear any previous feedback UI
    setShowFeedbackFor(null);

    // Add user message
    await addMessage.mutate({
      text: inputValue,
      selectedBuddyId, // Include the selectedBuddyId defined in this component
    });

    // Clear input field
    setInputValue("");

    // Scroll to the bottom after sending
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  }, [inputValue, addMessage, setShowFeedbackFor, selectedBuddyId]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        sendMessage();
      }
    },
    [sendMessage]
  );

  // Create a Blaze session
  const createBlazeSession = useCallback(
    async (query: string) => {
      try {
        // Navigate to blaze page with the query
        router.push({
          pathname: "/blaze",
          query: { topic: query },
        });
      } catch (error) {
        console.error("Error creating Blaze session:", error);
        // Show an error message to the user
        addAssistantResponseMutate(
          "I'm sorry, there was an error connecting you with a tutor. Please try again later."
        );
      }
    },
    [router, addAssistantResponseMutate]
  );

  // Get the last user query
  const getLastUserQuery = useCallback(() => {
    const userMessages = [...assistantMessages]
      .reverse()
      .filter((m) => m.sender === "user");
    return userMessages.length > 0 ? userMessages[0].text : "";
  }, [assistantMessages]);

  // Listen for follow-up question events from ChatMessage component
  useEffect(() => {
    // Handler for when a follow-up question is clicked
    const handleFollowUpQuestion = (event: CustomEvent) => {
      const { question } = event.detail;

      // Clear any previous feedback UI
      setShowFeedbackFor(null);

      // Directly send the message instead of just setting input value
      addMessage.mutate({
        text: question,
        selectedBuddyId, // Include the selectedBuddyId
      });

      // Scroll to the bottom after sending
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    };

    // Handler for when buddy switch is requested
    const handleSwitchBuddy = (event: CustomEvent) => {
      const { buddyId } = event.detail;

      console.log("🔄 Switching to buddy:", buddyId);

      // Use the buddy selection handler
      handleSelectBuddy(buddyId);

      // Clear any previous feedback UI
      setShowFeedbackFor(null);

      // Show a confirmation message
      // Optional: could add a brief message indicating the switch
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    };

    // Add event listener for the follow-up question event
    window.addEventListener(
      "assistantFollowUpQuestion",
      handleFollowUpQuestion as EventListener
    );

    // Add event listener for the buddy switch event
    window.addEventListener(
      "assistantSwitchBuddy",
      handleSwitchBuddy as EventListener
    );

    // Clean up event listeners when component unmounts
    return () => {
      window.removeEventListener(
        "assistantFollowUpQuestion",
        handleFollowUpQuestion as EventListener
      );
      window.removeEventListener(
        "assistantSwitchBuddy",
        handleSwitchBuddy as EventListener
      );
    };
  }, [addMessage, selectedBuddyId, setShowFeedbackFor, handleSelectBuddy]);

  // Keep existing effect for initial opening
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          block: "end",
          behavior: "auto",
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Update this effect to handle messages and feedback
  useEffect(() => {
    if (messagesEndRef.current && isOpen) {
      // Small delay to ensure feedback card is rendered
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [assistantMessages.length, showFeedbackFor, isOpen]);

  // Add another effect specifically for when feedback appears
  useEffect(() => {
    if (messagesEndRef.current && showFeedbackFor) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [showFeedbackFor]);

  // Add effect to detect when a message completes streaming and scroll to bottom
  useEffect(() => {
    // Only proceed if there are messages and the chat is open
    if (!isOpen || assistantMessages.length === 0) return;

    // Get the last message
    const lastMessage = assistantMessages[assistantMessages.length - 1];

    // Check if this is an assistant message that has completed streaming
    // and we haven't scrolled for this message yet
    if (
      lastMessage.sender === "assistant" &&
      lastMessage.id && // Ensure message has an id
      // !scrolledMessageIds.current.has(lastMessage.id) &&
      // (lastMessage.metadata?.streamComplete === true ||
      //   (lastMessage.metadata?.isStreaming === false &&
      //     lastMessage.metadata?.streamComplete !== false))
      true
    ) {
      // Add this message id to our set of scrolled messages
      scrolledMessageIds.current.add(lastMessage.id);

      // Message has completed streaming, scroll to bottom
      console.log("Message completed streaming, scrolling to bottom");
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [assistantMessages, isOpen]);

  // Track previous messages to detect streaming state changes
  const prevMessagesRef = useRef<Message[]>([]);

  // Add ref to track last scroll time for throttling
  const lastScrollTimeRef = useRef<number>(0);

  // Add effect specifically for real-time streaming auto-scroll
  useEffect(() => {
    if (!isOpen || assistantMessages.length === 0) return;

    // Get the last message
    const lastMessage = assistantMessages[assistantMessages.length - 1];

    // Check if this is an assistant message that is currently streaming
    if (
      lastMessage.sender === "assistant" &&
      lastMessage.metadata?.isStreaming === true &&
      lastMessage.text &&
      lastMessage.text.length > 10 // Only scroll if there's meaningful content
    ) {
      // Check if the text length has increased (new content arrived)
      const prevMessages = prevMessagesRef.current;
      if (prevMessages.length > 0) {
        const prevLastMessage = prevMessages[prevMessages.length - 1];

        if (
          prevLastMessage?.id === lastMessage.id &&
          (!prevLastMessage.text ||
            lastMessage.text.length > prevLastMessage.text.length)
        ) {
          // Throttle scrolling to avoid excessive operations (max once per 200ms)
          const now = Date.now();
          if (now - lastScrollTimeRef.current >= 200) {
            lastScrollTimeRef.current = now;

            // New content has been added to the streaming message, scroll smoothly
            console.log(
              "🔄 New streaming content detected, smooth scrolling..."
            );

            // Use a small delay to ensure DOM has updated
            const timer = setTimeout(() => {
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "end",
                });
              }
            }, 50);
          }
        }
      } else if (lastMessage.text.length > 0) {
        // First chunk of content for this streaming message
        console.log("🎬 First streaming content detected, smooth scrolling...");
        lastScrollTimeRef.current = Date.now();

        const timer = setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
              behavior: "smooth",
              block: "end",
            });
          }
        }, 50);
      }
    }

    // Update the previous messages reference
    prevMessagesRef.current = assistantMessages;
  }, [assistantMessages, isOpen, messagesEndRef]);

  // Add effect specifically to detect when isStreaming changes from true to false
  useEffect(() => {
    if (!isOpen || assistantMessages.length === 0) return;

    // Compare current messages with previous messages to detect streaming state changes
    const prevMessages = prevMessagesRef.current;
    prevMessagesRef.current = assistantMessages;

    // If this is first run, or we don't have enough previous messages, just return
    if (prevMessages.length === 0) return;

    // Find messages that just changed from streaming to not streaming
    assistantMessages.forEach((message, index) => {
      // Skip if no previous message at this index
      if (index >= prevMessages.length) return;

      const prevMessage = prevMessages[index];

      // Skip if message ids don't match (message was replaced)
      if (message.id !== prevMessage.id) return;

      // Check if message went from streaming to complete
      if (
        message.sender === "assistant" &&
        prevMessage.metadata?.isStreaming === true &&
        message.metadata?.isStreaming === false &&
        !scrolledMessageIds.current.has(`${message.id}_stream_complete`)
      ) {
        // Mark this streaming completion as handled
        scrolledMessageIds.current.add(`${message.id}_stream_complete`);

        // Scroll to bottom
        console.log(
          "Detected streaming -> complete transition, scrolling to bottom"
        );
        const timer = setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);

        return () => clearTimeout(timer);
      }
    });
  }, [assistantMessages, isOpen]);

  // Show feedback UI after assistant responds - memoized with appropriate dependencies
  const showFeedbackEffect = useCallback(() => {
    if (
      assistantMessages.length >= 2 &&
      !blazeQueryLoading &&
      !recommendationsLoading
    ) {
      const lastMessage = assistantMessages[assistantMessages.length - 1];
      const secondLastMessage = assistantMessages[assistantMessages.length - 2];

      if (
        lastMessage?.sender === "assistant" &&
        secondLastMessage?.sender === "user" &&
        !lastMessage.text.includes("What would you like to know today?") &&
        !lastMessage.text.includes("Great! I'm glad I could help") &&
        !lastMessage.text.includes(
          "Would you like to connect with a real tutor"
        )
      ) {
        const timer = setTimeout(() => {
          setShowFeedbackFor(lastMessage.id);
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [
    assistantMessages,
    blazeQueryLoading,
    recommendationsLoading,
    setShowFeedbackFor,
  ]);

  // Apply the effect
  useEffect(() => {
    return showFeedbackEffect();
  }, [showFeedbackEffect]);

  // Add effect to detect scroll position changes when new messages are added
  useEffect(() => {
    // When new messages are added, reset the scroll position
    if (assistantMessages.length > 0 && scrollPosition > 0) {
      // Small delay to let the messages render first
      const timer = setTimeout(() => {
        if (chatContainerRef.current) {
          // Get the current scroll position
          const currentScroll = chatContainerRef.current.scrollTop;
          // Only update if the scroll position has changed significantly
          if (Math.abs(currentScroll - scrollPosition) > 10) {
            setScrollPosition(currentScroll);
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [assistantMessages.length, scrollPosition]);

  // Generate random stars for the background
  const stars = useMemo(() => {
    const starsArray: StarStyle[] = [];
    for (let i = 0; i < 30; i++) {
      starsArray.push({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: `${Math.random() * 2 + 1}px`,
        height: `${Math.random() * 2 + 1}px`,
        opacity: Math.random() * 0.8 + 0.2,
        animationDuration: `${Math.random() * 5 + 3}s`,
      });
    }
    return starsArray;
  }, []);

  // Memoized background component
  const CosmicBackground = React.memo(({ stars }: { stars: StarStyle[] }) => (
    <div
      className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none"
      style={{ touchAction: "none" }}
    >
      {stars.map((star, index) => (
        <Star key={index} style={{ ...star, pointerEvents: "none" }} />
      ))}

      {/* Add planets */}
      <Planet
        type="1"
        style={{
          top: "10%",
          right: "-5%",
          width: "60px",
          height: "60px",
          opacity: 0.7,
          pointerEvents: "none",
        }}
        className="planet-float-1"
      />

      <Planet
        type="3"
        style={{
          bottom: "15%",
          left: "-3%",
          width: "40px",
          height: "40px",
          opacity: 0.6,
          pointerEvents: "none",
        }}
        className="planet-float-2"
      />

      {/* Add small astronaut - repositioned and made more subtle */}
      <div className="absolute bottom-[15%] right-[8%] w-[50px] h-[50px] pointer-events-none opacity-40 z-10">
        <AstronautLottie
          style={{ width: "100%", height: "100%", pointerEvents: "none" }}
        />
      </div>
    </div>
  ));
  CosmicBackground.displayName = "CosmicBackground";

  // Handle clicks outside the assistant
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        assistantRef.current && // Use assistantRef instead of messagesEndRef
        !assistantRef.current.contains(event.target as Node)
      ) {
        // Check if the click occurred on the AssistantTrigger button using its ID
        const triggerButton = document.getElementById(
          "assistant-trigger-button"
        );
        if (
          triggerButton &&
          (triggerButton === event.target ||
            triggerButton.contains(event.target as Node))
        ) {
          // Prevent the assistant from closing if the click is on the trigger button
          // Let the onClick handler of the trigger button handle the toggling
          return;
        }

        setIsOpen(false);
      }
    };

    // Handle link clicks within the assistant
    const handleLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isLink = target.tagName === "A" || target.closest("a");

      if (isOpen && isLink) {
        // Small delay to allow the link navigation to start
        setTimeout(() => {
          setIsOpen(false);
        }, 100);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("click", handleLinkClick);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("click", handleLinkClick);
    };
  }, [isOpen]);

  // Handle route changes (for when links navigate within the app)
  useEffect(() => {
    const handleRouteChange = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router, isOpen]);

  // Add welcome message if opening chat for the first time and no messages exist
  // Commenting out the welcome message feature
  /* 
  useEffect(() => {
    if (isOpen && assistantMessages.length === 0) {
      addAssistantResponseMutate(
        "✨ *Welcome to PuStack AI!*\n\nI'm your personalized learning assistant! I can help with:\n• Solving complex homework problems\n• Explaining difficult concepts clearly\n• Finding study resources tailored to you\n• Connecting you with expert tutors\n• Customizing your learning experience\n• Exploring PuStack Premium features\n\nWhat would you like help with today? 🚀"
      );
    } else if (isOpen && assistantMessages.length > 0 && !blazeQueryLoading) {
      // If reopening a chat that already has messages, add a contextual welcome back message
      const lastMessageTime =
        assistantMessages[assistantMessages.length - 1]?.timestamp;
      if (lastMessageTime) {
        const lastMessageDate = new Date(lastMessageTime);
        const now = new Date();
        const timeDifference = now.getTime() - lastMessageDate.getTime();
        const minutesSinceLastMessage = Math.floor(
          timeDifference / (1000 * 60)
        );

        // Only add a welcome back message if it's been more than 10 minutes
        if (minutesSinceLastMessage > 10) {
          let welcomeBackMessage =
            "✨ *Welcome back!*\n\nReady to continue learning? I can help with:\n• Tackling challenging problems\n• Exploring new subjects\n• Finding valuable study resources\n• Connecting with subject experts\n\nWhat are you working on today? 🚀";

          // Get the subject of the last conversation if available
          const lastSubject =
            assistantMessages[assistantMessages.length - 1]?.metadata
              ?.detected_subject;
          if (lastSubject && lastSubject !== "General") {
            welcomeBackMessage = `✨ *Welcome back!*\n\nWe previously explored ${lastSubject} together.\n• Continue with ${lastSubject}\n• Dive into a new topic\n• Get personalized study resources\n• Connect with a subject expert\n\nHow would you like to continue your learning journey? 🚀`;
          }

          addAssistantResponseMutate(welcomeBackMessage);
        }
      }
    }
  }, [
    isOpen,
    assistantMessages.length,
    addAssistantResponseMutate,
    blazeQueryLoading,
  ]);
  */

  // Handle mobile viewport and keyboard visibility
  useEffect(() => {
    if (!isMobile) return;

    // When keyboard appears on mobile, we want to make sure the input stays visible
    const handleResize = () => {
      // Force scroll to input when keyboard appears
      if (isKeyboardVisible && isOpen) {
        // Small delay to ensure the keyboard is fully opened
        setTimeout(() => {
          // Scroll the chat container to bottom instead of the whole window
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop =
              chatContainerRef.current.scrollHeight;
          }
          // Also ensure the input is visible
          if (chatInputRef.current) {
            chatInputRef.current.focus?.();
          }
        }, 100);
      }
    };

    window.addEventListener("resize", handleResize);

    // Set meta viewport to prevent zooming and ensure proper display
    const metaViewport = document.querySelector("meta[name=viewport]");
    const originalContent = metaViewport?.getAttribute("content");

    if (isKeyboardVisible && isOpen) {
      // Ensure no scaling when keyboard is open and use dynamic viewport height
      metaViewport?.setAttribute(
        "content",
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
      );
    } else if (originalContent) {
      // Restore original viewport when keyboard is closed
      metaViewport?.setAttribute("content", originalContent);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      // Restore original viewport on cleanup
      if (originalContent) {
        metaViewport?.setAttribute("content", originalContent);
      }
    };
  }, [isMobile, isKeyboardVisible, isOpen]);

  // Add this effect to reset tool execution state when reopening
  useEffect(() => {
    if (isOpen) {
      // Reset tool execution state when opening the assistant
      setToolExecution({
        isExecuting: false,
        toolName: "",
      });
      setAssistantState((prev) => ({
        ...prev,
        status: "idle",
      }));
    }
  }, [isOpen]);

  return (
    <div
      style={{ zIndex: 999, position: "relative" }}
      className="assistant-container"
    >
      <MemoizedAssistantTrigger onClick={toggleChat} isOpen={isOpen} />
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Full-screen backdrop blur overlay */}
            <motion.div
              className="fixed inset-0 z-[999998] pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                backdropFilter: "blur(2px)",
                WebkitBackdropFilter: "blur(2px)",
                backgroundColor: "rgba(0, 0, 0, 0.1)",
              }}
            />

            {/* Assistant container */}
            <motion.div
              ref={assistantRef}
              className={`fixed z-[999999] flex ${
                isMobile
                  ? "inset-0 flex-col assistant-mobile-layout"
                  : "bottom-20 right-4 left-auto flex-row"
              }`}
              initial={
                isMobile
                  ? { opacity: 0, translateY: "100%" }
                  : { opacity: 0, y: 20, scale: 0.95 }
              }
              animate={
                isMobile
                  ? { opacity: 1, translateY: "0%" }
                  : { opacity: 1, y: 0, scale: 1 }
              }
              exit={
                isMobile
                  ? { opacity: 0, translateY: "100%" }
                  : { opacity: 0, y: 10, scale: 0.95 }
              }
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              style={{
                height: isMobile ? "100vh" : "80vh",
                width: isMobile ? "100%" : isExpanded ? "1015px" : "auto",
                justifyContent: "flex-end",
                overscrollBehavior: "none",
                // Use dynamic viewport height for better mobile support
                ...(isMobile && {
                  height: "100dvh", // Dynamic viewport height
                  maxHeight: "100dvh",
                }),
              }}
              onWheel={(e) => e.stopPropagation()}
            >
              {/* Remove the local background blur overlay since we now have full-screen blur */}
              {/* <div
                className="absolute inset-0 rounded-lg pointer-events-none"
                style={{
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                  zIndex: -1,
                }}
              /> */}

              {/* Left Pane - Buddies List (on mobile, it's a horizontal row at the top) */}
              {isMobile ? (
                <div
                  className="h-[90px] min-h-[90px] w-full buddies-panel p-2 bg-[#0a0a0a] border-b border-[#404040] shadow-lg backdrop-blur-sm relative"
                  style={{
                    backgroundColor: "rgba(10, 10, 10, 0.95)", // Semi-transparent
                    boxShadow:
                      "0 8px 25px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.05)", // Subtle depth
                  }}
                >
                  {/* Close button for mobile */}
                  <motion.button
                    className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-[#1a1a1a] border border-[#404040] flex items-center justify-center text-[#888] hover:text-[#fff] hover:bg-[#2a2a2a] transition-all duration-200"
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setIsOpen(false)}
                    style={{
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </motion.button>

                  <div className="flex overflow-x-auto hide-scrollbar gap-3 items-center h-full pl-1 pr-12">
                    <div className="ml-2 text-[#d7d7d7] text-xs font-semibold flex items-center">
                      <span className="mr-1.5">✨</span>
                      <span className="text-[#3ea6ff]">Pick:</span>
                    </div>
                    {[
                      {
                        id: "explainer",
                        name: "Concept Explainer",
                        icon: "📚",
                        description:
                          "Detailed explanations of any academic concept",
                        bgColor: "bg-gradient-to-br from-cyan-500 to-blue-600",
                      },
                      {
                        id: "blaze",
                        name: "Blaze Sessions",
                        icon: "👨‍🏫",
                        description: "1-on-1 expert tutoring for any subject",
                        bgColor: "bg-gradient-to-br from-orange-500 to-red-600",
                      },
                      {
                        id: "live",
                        name: "Class Finder",
                        icon: "🎬",
                        description: "Find cool live classes to join",
                        bgColor:
                          "bg-gradient-to-br from-green-500 to-emerald-700",
                      },
                      {
                        id: "search",
                        name: "Video Lectures",
                        icon: "🎥",
                        description: "Watch videos explaining tough topics",
                        bgColor:
                          "bg-gradient-to-br from-blue-500 to-indigo-600",
                      },
                      {
                        id: "learning",
                        name: "Study Guide",
                        icon: "📝",
                        description: "Step-by-step guides to master any topic",
                        bgColor:
                          "bg-gradient-to-br from-purple-500 to-pink-600",
                      },
                      {
                        id: "settings",
                        name: "Settings Helper",
                        icon: "⚙️",
                        description: "Change your theme or grade level",
                        bgColor: "bg-gradient-to-br from-gray-500 to-gray-700",
                      },
                    ].map((buddy) => (
                      <motion.div
                        key={buddy.id}
                        className={`flex-shrink-0 flex flex-col items-center justify-center cursor-pointer ${
                          selectedBuddyId === buddy.id ? "scale-110" : ""
                        }`}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleSelectBuddy(buddy.id)}
                      >
                        <motion.div
                          className={`w-11 h-11 rounded-full flex items-center justify-center relative overflow-hidden ${
                            selectedBuddyId === buddy.id
                              ? "ring-2 ring-[#3ea6ff]/70 shadow-lg shadow-[#3ea6ff]/40"
                              : "border border-[#3ea6ff]/30 hover:border-[#3ea6ff]/50"
                          }`}
                          animate={{
                            boxShadow:
                              selectedBuddyId === buddy.id
                                ? [
                                    "0 0 0 rgba(62, 166, 255, 0.5)",
                                    "0 0 15px rgba(62, 166, 255, 0.8)",
                                    "0 0 0 rgba(62, 166, 255, 0.5)",
                                  ]
                                : [
                                    "0 0 0 rgba(255, 255, 255, 0)",
                                    "0 0 0 rgba(255, 255, 255, 0)",
                                  ],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                          style={{
                            background: `linear-gradient(135deg, ${
                              selectedBuddyId === buddy.id
                                ? "#2563eb, #4f46e5"
                                : "#1f1f1f, #161616"
                            })`,
                          }}
                        >
                          <motion.div
                            initial={{ scale: 1 }}
                            animate={{
                              scale:
                                selectedBuddyId === buddy.id ? [1, 1.2, 1] : 1,
                              rotate:
                                selectedBuddyId === buddy.id
                                  ? [0, 5, 0, -5, 0]
                                  : 0,
                            }}
                            transition={{
                              duration: 2,
                              repeat:
                                selectedBuddyId === buddy.id ? Infinity : 0,
                              ease: "easeInOut",
                            }}
                            className="text-xl drop-shadow-md"
                          >
                            {buddy.icon}
                          </motion.div>

                          {/* Animated shadow pulse when selected */}
                          {selectedBuddyId === buddy.id && (
                            <motion.div
                              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                              animate={{ x: ["-100%", "100%"] }}
                              transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                ease: "easeInOut",
                                repeatDelay: 0.5,
                              }}
                            />
                          )}
                        </motion.div>
                        {/* Show name below the icon for mobile view */}
                        {selectedBuddyId === buddy.id && (
                          <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="w-full text-center text-[9px] text-[#3ea6ff] font-semibold mt-0.5 drop-shadow-md"
                          >
                            {buddy.name}
                          </motion.div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="w-[200px] min-w-[200px] mr-3 transition-all duration-300 bg-[#0f0f0f] rounded-lg border border-[#272727] overflow-hidden shadow-lg">
                  <BuddiesList
                    onSelectBuddy={handleSelectBuddy}
                    selectedBuddyId={selectedBuddyId}
                    onToggleExpand={handleToggleExpand}
                    isExpanded={isExpanded}
                  />
                </div>
              )}

              {/* Assistant Chat Panel */}
              <div
                className="flex flex-col md:rounded-lg shadow-lg border border-[#404040] overflow-hidden assistant-main-panel backdrop-blur-sm"
                style={{
                  width: isMobile ? "100%" : isExpanded ? "800px" : "600px",
                  maxWidth: isMobile ? "100%" : isExpanded ? "800px" : "600px",
                  height: isMobile ? "calc(100% - 90px)" : "80vh",
                  maxHeight: isMobile ? "calc(100% - 90px)" : "80vh",
                  transition:
                    "width 0.3s ease-in-out, max-width 0.3s ease-in-out",
                  backgroundColor: "rgba(15, 15, 15, 0.95)", // Semi-transparent for better layering
                  boxShadow:
                    "0 20px 60px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.1)", // Deep shadow with subtle rim light
                }}
              >
                <CosmicBackground stars={stars} />

                {/* <MemoizedChatHeader
                  toggleChat={toggleChat}
                  isMobile={isMobile}
                  isCompact={scrollPosition > 50}
                /> */}

                {/* Main Chat Area */}
                <motion.div
                  ref={chatContainerRef}
                  className={`flex-1 overflow-y-auto overflow-x-hidden p-3 md:p-5 space-y-1 relative chat-scroll-area ${
                    isMobile
                      ? "pb-4 assistant-chat-flex"
                      : "max-h-[calc(80vh-80px)]"
                  }`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  style={{
                    flexGrow: 1,
                    flexShrink: 1,
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                    WebkitOverflowScrolling: "touch",
                    willChange: "scroll-position",
                    touchAction: "pan-y",
                    // backgroundColor: "#0f0f0f", // YouTube-like background
                    backgroundImage:
                      "url('/assets/pustackCareChat/chat_wallpaper_dark.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    // For mobile, ensure proper height calculation
                    height: isMobile ? "calc(100% - 80px)" : "auto",
                  }}
                  onScroll={(e) => {
                    setScrollPosition(e.currentTarget.scrollTop);
                  }}
                >
                  {/* Always display SelectedBuddyInfo, even for default buddy */}
                  <SelectedBuddyInfo selectedBuddyId={selectedBuddyId} />

                  {isLoading ? (
                    <div className="flex justify-center items-center h-full">
                      <div className="relative w-12 h-12">
                        <div className="absolute inset-0 rounded-full border-4 border-gray-700"></div>
                        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-gray-300 animate-spin"></div>
                        <div className="absolute inset-[15%] rounded-full bg-black flex items-center justify-center">
                          <div className="w-1 h-1 bg-white rounded-full animate-ping"></div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <MessageList
                      messages={assistantMessages}
                      showFeedbackFor={showFeedbackFor}
                      setShowFeedbackFor={setShowFeedbackFor}
                      addAssistantResponseMutate={addAssistantResponseMutate}
                      createBlazeSession={createBlazeSession}
                      getLastUserQuery={getLastUserQuery}
                      blazeQueryLoading={blazeQueryLoading}
                      recommendationsLoading={recommendationsLoading}
                      requestCancelled={requestCancelled}
                      retryResourceFetch={retryResourceFetch}
                      messagesEndRef={messagesEndRef}
                      handleToolCall={handleToolCall}
                      selectedBuddyId={selectedBuddyId}
                      handleSelectBuddy={handleSelectBuddy}
                    />
                  )}
                </motion.div>

                {/* Chat Input - Always at bottom of the panel, not fixed to viewport */}
                <div
                  className={`assistant-input-area ${
                    isMobile
                      ? "bg-[#0f0f0f] border-t border-[#272727] flex-shrink-0"
                      : ""
                  }`}
                  style={{
                    // Ensure input area has proper height and is always visible
                    minHeight: isMobile ? "80px" : "auto",
                    maxHeight: isMobile ? "120px" : "auto",
                  }}
                >
                  <ChatInput
                    ref={chatInputRef}
                    inputValue={inputValue}
                    handleInputChange={handleInputChange}
                    handleKeyPress={handleKeyPress}
                    sendMessage={sendMessage}
                    isLoading={isLoading || blazeQueryLoading}
                    isMobile={isMobile}
                    interruptRequest={interruptRequest}
                  />
                </div>
              </div>

              {/* Tool execution animation overlay */}
              <AnimatePresence>
                {toolExecution.isExecuting && (
                  <ToolExecutionAnimation toolName={toolExecution.toolName} />
                )}
              </AnimatePresence>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Add global styles for animations */}
      <style jsx>{`
        .assistant-container {
          /* Scoped keyframe animations */
        }

        @keyframes assistant-float {
          0% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(5deg);
          }
          100% {
            transform: translateY(0px) rotate(0deg);
          }
        }

        .assistant-container .blinking-star {
          animation-name: assistant-blink;
          animation-iteration-count: infinite;
          animation-direction: alternate;
        }

        @keyframes assistant-blink {
          0% {
            opacity: 0.2;
          }
          100% {
            opacity: 1;
          }
        }

        /* Scoped mobile styles - only for assistant */
        .assistant-container .assistant-main-panel {
          transition: width 0.3s ease-in-out, max-width 0.3s ease-in-out;
          backgroundcolor: rgba(15, 15, 15, 0.95);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8),
            0 0 0 1px rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
        }

        .assistant-container .assistant-main-panel .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .assistant-container
          .assistant-main-panel
          .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }

        /* Scoped scrollbar styles - only for assistant chat area */
        .assistant-container .chat-scroll-area {
          scrollbar-width: thin;
          scrollbar-color: rgba(160, 160, 160, 0.75) transparent;
        }

        .assistant-container .chat-scroll-area::-webkit-scrollbar {
          width: 8px;
        }

        .assistant-container .chat-scroll-area::-webkit-scrollbar-track {
          background: transparent;
        }

        .assistant-container .chat-scroll-area::-webkit-scrollbar-thumb {
          background-color: rgba(255, 255, 255, 0.2);
          border-radius: 20px;
          border: 3px solid transparent;
        }

        /* Scoped panel shadows */
        .assistant-container .buddies-panel {
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.7),
            0 0 0 1px rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(10px);
        }

        /* Scoped animation optimizations */
        .assistant-container .animate-optimize {
          backface-visibility: hidden;
          transform: translateZ(0);
          perspective: 1000px;
        }

        /* Scoped text colors - only within assistant */
        .assistant-container .assistant-text-primary {
          color: #f1f1f1;
        }

        .assistant-container .assistant-text-secondary {
          color: #aaaaaa;
        }

        /* Scoped buddy interactions */
        .assistant-container .buddy-icon {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .assistant-container .buddy-icon:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .assistant-container .selected-buddy {
          background-color: #272727;
        }

        /* Scoped star animation */
        .assistant-container .blinking-star {
          animation-duration: 4s;
        }

        /* Planet floating animation scoped to assistant */
        .assistant-container .planet-float-1 {
          animation: assistant-float 8s ease-in-out infinite;
        }

        .assistant-container .planet-float-2 {
          animation: assistant-float 6s ease-in-out infinite;
        }

        /* Mobile-specific styles only for assistant */
        @media screen and (max-width: 767px) {
          .assistant-container .assistant-mobile-layout {
            height: 100vh;
            height: 100dvh;
            max-height: 100vh;
            max-height: 100dvh;
            display: flex;
            flex-direction: column;
          }

          .assistant-container .assistant-chat-flex {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
          }

          .assistant-container .assistant-input-area {
            flex-shrink: 0;
            position: relative;
            bottom: 0;
          }

          /* Prevent zoom on assistant inputs only */
          .assistant-container input,
          .assistant-container textarea,
          .assistant-container select {
            font-size: 16px;
          }
        }
      `}</style>

      {/* Scoped styles for better text readability - only within assistant */}
      <style jsx>{`
        .assistant-container {
          font-size: 16px;
        }

        .assistant-container .follow-up-question {
          font-size: 0.95rem;
          padding: 0.6rem;
        }

        .assistant-container .message-timestamp {
          color: #aaaaaa;
          font-size: 0.8rem;
        }

        .assistant-container .assistant-input {
          font-size: 1rem;
          color: #f1f1f1;
        }

        .assistant-container button,
        .assistant-container [role="button"] {
          color: #f1f1f1;
        }

        .assistant-container p,
        .assistant-container div {
          line-height: 1.5;
        }

        .assistant-container .message-content p {
          margin-bottom: 0.75em;
        }

        .assistant-container .message-content p:last-child {
          margin-bottom: 0;
        }

        .assistant-container .buddies-panel {
          background-color: #0f0f0f;
        }

        .assistant-container .buddies-panel h3,
        .assistant-container .buddies-panel h4 {
          color: #f1f1f1;
        }

        .assistant-container .buddies-panel p {
          color: #aaaaaa;
        }
      `}</style>
    </div>
  );
}

// Add ToolExecutionAnimation component before the AssistantContent function
// Memoized Tool Execution Animation component
const ToolExecutionAnimation = React.memo(
  ({ toolName }: { toolName: string }) => (
    <motion.div
      className="absolute inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="bg-gray-900/80 p-6 rounded-lg border border-gray-700 shadow-lg max-w-md text-center">
        <div className="flex justify-center mb-3">
          <div className="w-16 h-16 relative">
            <motion.div
              className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 opacity-70"
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "linear",
              }}
            />
            <motion.div
              className="absolute inset-[15%] rounded-full flex items-center justify-center"
              animate={{ rotate: [0, -360] }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "linear",
              }}
            >
              <span className="text-2xl">
                {toolName === "Maps_to_page" ? "🚀" : "⚙️"}
              </span>
            </motion.div>
          </div>
        </div>
        <h3 className="text-white text-lg font-medium mb-2">
          {toolName === "Maps_to_page"
            ? "Taking you there..."
            : `Executing ${toolName}...`}
        </h3>
        <p className="text-gray-300 text-sm">
          {toolName === "Maps_to_page"
            ? "Preparing to navigate to the requested page"
            : "Please wait while I process your request"}
        </p>
      </div>
    </motion.div>
  )
);
ToolExecutionAnimation.displayName = "ToolExecutionAnimation";

// Fix the main Assistant function to also include selectedBuddyId state
export default function Assistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showFeedbackFor, setShowFeedbackFor] = useState<string | null>(null);
  const [rocketLaunch, setRocketLaunch] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<ChatInputHandle>(null);
  const assistantRef = useRef<HTMLDivElement>(null); // Add this new ref
  const [inputValue, setInputValue] = useState("");
  const [scrollPosition, setScrollPosition] = useState(0); // Track scroll position
  const chatContainerRef = useRef<HTMLDivElement>(null); // Ref for chat area
  // Add selectedBuddyId state in the main component too
  const [selectedBuddyId, setSelectedBuddyId] = useState<string | null>(
    "explainer" // Changed from "default" to "explainer"
  );
  // Add isExpanded state in the main component
  const [isExpanded, setIsExpanded] = useState(false);

  // Add handleSelectBuddy function in the main component
  const handleSelectBuddy = useCallback((buddyId: string) => {
    setSelectedBuddyId(buddyId);
  }, []);

  return (
    <AssistantToolsProvider>
      <AssistantContent
        chatContainerRef={chatContainerRef}
        messagesEndRef={messagesEndRef}
        scrollPosition={scrollPosition}
        setScrollPosition={setScrollPosition}
        selectedBuddyId={selectedBuddyId}
        handleSelectBuddy={handleSelectBuddy}
        isExpanded={isExpanded}
        handleToggleExpand={() => setIsExpanded(!isExpanded)}
      />
    </AssistantToolsProvider>
  );
}
